<template>
  <div
    class="asset-module__wrapper"
    :class="{
      'asset-module__wrapper--transparent': moduleData.options.use_transparent_background,
      'component-module-wrapper__module-contents--shape-mask': moduleData
        && moduleData.options && moduleData.options.shape
        && moduleData.options.shape !== 'default',
    }"
  >
    <a
      v-if="showAssetStatusWarning"
      :href="viewInContentUrl"
      target="_blank"
      class="asset-module__asset-status-warning"
    >
      <NebulaBadge
        class="asset-module__asset-status-warning-badge"
        color="warning"
      >
        <NebulaIcon
          class="asset-module__asset-status-warning-icon"
          symbol-id="warning"
        />
        <span class="asset-module__asset-status-warning-text">
          {{ assetStatusDescription }}
        </span>
      </NebulaBadge>
    </a>
    <template
      v-if="style === 'link'"
    >
      <a
        v-if="asset && asset.type"
        :href="getAssetLink(asset.id)"
        target="_blank"
        class="asset-module__link"
      >
        {{ asset.title }}
      </a>
      <LoadingIndicator
        absolute
        v-else
      />
    </template>
    <template
      v-else
    >
      <ModuleError
        v-if="asset && asset.error"
        :error="asset.error"
        :asset-id="asset.id"
      />
      <MediaViewer
        ref="media"
        v-else-if="asset && asset.type"
        :active="active"
        :asset-group="asset.group"
        :asset-id="asset.id"
        :autoplay="isAutoplay"
        :loop="loopVideo"
        :module-data="moduleData"
        :thumbnail-url="assetThumbnailUrl"
        :mode="mode"
        :media-url="mediaUrl"
        :title="asset.title"
        :description="asset.description"
        :player-link="usePlayer ? playerLink : ''"
        :type="asset.type.name"
        :icon-id="iconId"
        :scale="scale"
        :show-title="moduleData.options.show_title"
        :show-description="moduleData.options.show_description"
        :show-type="moduleData.options.show_type"
        :use-transparent-background="moduleData.options.use_transparent_background"
        :video="video"
        :canvas-name="canvasName"
        :is-displayed="isDisplayed"
        :theme-name="themeName"
        :start-time="durationToSeconds('moduleData.options.video_start')"
        :stop-time="durationToSeconds('moduleData.options.video_end')"
        @video-play="suppressAssetStatusWarning = true"
        @video-pause="suppressAssetStatusWarning = false"
        @video-ended="onVideoEnded"
      />
      <LoadingIndicator
        absolute
        v-else
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { get } from 'lodash-es';
import { NebulaIcon, NebulaBadge } from '@discoveryedu/nebula-components';
import { MP3_GENERAL_AUDIO_FORMAT_ID } from '@/lib/constants/mediaFileFormatIds';
import { ASSET_STATUS_ACTIVE } from '@/lib/constants';
import { bus } from '@/lib/eventBus';
import assetMixin from '@/mixins/asset';
import MediaViewer from '@/components/MediaViewer.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import ModuleError from '@/components/modules/ModuleError.vue';
import * as types from '@/lib/constants/store';
import {
  useAssetStore,
  useEditorStore,
  useUserStore,
} from '@/stores';
import { getAppDomain } from '@/lib/api';
import { durationToSeconds } from '../../lib/utils';

export default {
  name: 'AssetModule',
  components: {
    LoadingIndicator,
    MediaViewer,
    ModuleError,
    NebulaIcon,
    NebulaBadge,
  },
  mixins: [assetMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    canvasName: {
      type: String,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
    active: {
      type: Boolean,
      default: false,
    },
    isDisplayed: {
      type: Boolean,
      default: true,
    },
    page: {
      type: Object,
      default: null,
    },
    style: {
      type: String,
      default: 'card',
    },
  },
  data() {
    return {
      suppressAssetStatusWarning: false,
    };
  },
  created() {
    bus.on('editor-canvas-carousel:start-page-change-animation', this.onStartPageChangeAnimation);
  },
  beforeUnmount() {
    bus.off('editor-canvas-carousel:start-page-change-animation', this.onStartPageChangeAnimation);
  },
  watch: {
    assetId() {
      this.suppressAssetStatusWarning = false;
    },
    isDisplayed() {
      /*
        When this module is no longer displayed (in carousel but not visible),
        then reset audio and video to beginning.
      */
      if (!this.isDisplayed) {
        const { media } = this.$refs;
        if (media) {
          media.reset();
        }
      }
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_NEXT_PAGE,
    ]),
    durationToSeconds,
    onStartPageChangeAnimation() {
      /*
        When this module is animating out of view, ensure
        that the audio or video is paused.
      */
      const { media } = this.$refs;
      if (media) {
        media.pause();
      }
    },
    onVideoEnded() {
      if (this.autoAdvance) {
        this[types.VIEW_NEXT_PAGE]();
      }
    },
    getAssetLink(assetId) {
      return `${getAppDomain()}/player/${assetId}`;
    },
  },
  computed: {
    ...mapState(useAssetStore, [
      'assets',
    ]),
    ...mapState(useEditorStore, [
      'currentPageIndex',
      'currentPage',
      'editorIsFullscreen',
    ]),
    ...mapState(useUserStore, [
      'isDeUser',
    ]),
    isAutoplay() {
      const autoPlay = get(this, 'moduleData.options.auto_play', false);
      return autoPlay && this.isDisplayed;
    },
    loopVideo() {
      const loop = this.editorIsFullscreen && get(this, 'moduleData.options.loop_video', false);
      return loop && this.isDisplayed;
    },
    autoAdvance() {
      const autoAdvance = this.editorIsFullscreen && get(this, 'moduleData.options.auto_advance', false);
      return autoAdvance && this.isDisplayed;
    },
    assetId() {
      return get(this, 'moduleData.options.asset_id');
    },
    asset() {
      if (!this.assetId) return null;
      return this.assets[this.assetId];
    },
    mediaUrl() {
      // For audio, return the associated media file
      if (this.asset.group === 'audio') {
        const audioFile = this.asset.media_files
          .find((mediaFile) => mediaFile.format_id === MP3_GENERAL_AUDIO_FORMAT_ID);

        return audioFile && audioFile.url;
      }

      return null;
    },
    iconId() {
      return `asset-${this.asset.type.name.toLowerCase().replace(/ /g, '-')}`;
    },
    video() {
      if (this.asset.group !== 'video' || !this.assetVideoMediaFiles) return undefined;

      return {
        ...this.asset,
        media_files: this.assetVideoMediaFiles,
        closed_captions: this.assetClosedCaptions,
        duration_in_seconds: this.asset.duration,
      };
    },
    showAssetStatusWarning() {
      return this.isDeUser && !this.suppressAssetStatusWarning && this.assetStatus
        && this.assetStatus !== ASSET_STATUS_ACTIVE;
    },
    themeName() {
      return (this.page || this.currentPage)?.options?.theme?.name;
    },
  },
};
</script>

<style lang="stylus">

.asset-module {
  break-inside: avoid; // Avoid splitting across pages when printing

  &__wrapper {
    background-color: $nebula-color-white;
    height: 100%;

    &--transparent {
      background-color: transparent;
    }
  }

  &__link {
    text-decoration: underline;
    font-family: Proxima Vara;
    color: $nebula-color-platform-interactive-850;

    &:hover {
      color: $nebula-color-platform-interactive-900;
      text-decoration: underline;
    }

    &:active {
      text-decoration: underline;
      color: $nebula-color-platform-interactive-1000;
    }
  }

  &__asset-status-warning {
    max-width: "calc(100% - %s)" % $nebula-space-2x;
    position: absolute;
    right: $nebula-space-1x;
    text-decoration: none;
    top: $nebula-space-1x;
    z-index: 1;
  }

  &__asset-status-warning-badge {
    min-width: auto;
  }

  &__asset-status-warning-icon {
    fill: $nebula-color-white;
    flex: 0 0 $nebula-space-3x;
    margin-right: $nebula-space-1x;
  }

  &__asset-status-warning-text {
    fill: $nebula-color-white;
    margin-right: $nebula-space-1x;
  }

  &__button {
    margin: $nebula-space-1x;
  }
}

.nebula-badge--warning {
  background-color: $nebula-color-warning;
  color: $nebula-color-white;
}

</style>
